import type { LocationPoint } from './planning.ts'
import { newYorkDateTime } from './planner-time.ts'
import type { ScheduledModeSet } from './multimodal/protocol.ts'
import type { RankingPreference } from './transit/candidates.ts'
import type { TravelChoice, TravelMode, TravelPermissionSet } from './transit/modes.ts'

interface PlannerDraftFields {
  venueType: 'coffee' | 'brunch' | 'drinks'
  preference: RankingPreference
  maximumMinutes: 45 | 60 | 90 | null
  departureDate: string
  departureTime: string
}

export interface CurrentPlannerDraft extends PlannerDraftFields {
  version: 4
  timeIntent: 'depart-at' | 'arrive-by'
  leaveNow?: true
  origins: Array<{
    point: LocationPoint
    choice: Exclude<TravelChoice, { kind: 'none' }>
  }>
}

interface LegacyPlannerDraftFields extends Omit<PlannerDraftFields, 'departureDate'> {
  departureDate: string | null
}

interface LegacyPlannerDraftV1 extends LegacyPlannerDraftFields {
  version: 1
  origins: Array<{
    point: LocationPoint
    choice:
      | { kind: 'scheduled'; modes: ScheduledModeSet; bicycleAccess?: boolean; bikeShareAccess?: boolean }
      | { kind: 'road'; mode: 'taxi' | 'bike' }
      | { kind: 'bike-share' }
  }>
}

interface LegacyPlannerDraftV2 extends LegacyPlannerDraftFields {
  version: 2
  origins: CurrentPlannerDraft['origins']
}

interface LegacyPlannerDraftV3 extends PlannerDraftFields {
  version: 3
  timeIntent: 'depart-at'
  origins: CurrentPlannerDraft['origins']
}

export type PlannerDraft = CurrentPlannerDraft | LegacyPlannerDraftV1 | LegacyPlannerDraftV2
  | LegacyPlannerDraftV3

const travelModes = ['walk', 'subway', 'bus', 'ferry', 'taxi', 'bike', 'bike-share'] as const
const scheduledModes = ['subway', 'bus', 'ferry'] as const

export function encodePlannerDraft(draft: PlannerDraft): string {
  const valid = parsePlannerDraftValue(draft)
  const bytes = new TextEncoder().encode(JSON.stringify(valid))
  return btoa(String.fromCharCode(...bytes)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '')
}

export function decodePlannerDraft(encoded: string): CurrentPlannerDraft {
  try {
    if (encoded.length > 10_000) invalid('the plan is too large.')
    const base64 = encoded.replaceAll('-', '+').replaceAll('_', '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const binary = atob(padded)
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    return parsePlannerDraftValue(JSON.parse(new TextDecoder().decode(bytes)))
  } catch (cause) {
    if (cause instanceof Error && cause.message.startsWith('Invalid planner draft:')) throw cause
    throw new Error('Invalid planner draft: could not decode the plan.')
  }
}

function parsePlannerDraftValue(value: unknown): CurrentPlannerDraft {
  if (!isRecord(value) || ![1, 2, 3, 4].includes(value.version as number)) {
    invalid('unsupported version.')
  }
  if (!Array.isArray(value.origins) || value.origins.length < 2 || value.origins.length > 6) {
    invalid('choose between two and six origins.')
  }
  const origins = value.origins.map((origin) => {
    if (!isRecord(origin)) invalid('an origin is malformed.')
    return {
      point: parsePoint(origin.point),
      choice: value.version === 1 ? migrateLegacyChoice(origin.choice) : parseChoice(origin.choice),
    }
  })
  if (value.venueType !== 'coffee' && value.venueType !== 'brunch' && value.venueType !== 'drinks') {
    invalid('unknown venue type.')
  }
  if (value.preference !== 'balanced' && value.preference !== 'fairest' && value.preference !== 'fastest') {
    invalid('unknown preference.')
  }
  if (![45, 60, 90, null].includes(value.maximumMinutes as 45 | 60 | 90 | null)) {
    invalid('unknown trip limit.')
  }
  if (value.departureDate !== null
    && (typeof value.departureDate !== 'string' || !/^\d{8}$/.test(value.departureDate))) {
    invalid('departure date must use YYYYMMDD.')
  }
  if ((value.version === 3 && value.timeIntent !== 'depart-at')
    || (value.version === 4 && value.timeIntent !== 'depart-at' && value.timeIntent !== 'arrive-by')
    || (value.version === 3 || value.version === 4) && value.departureDate === null) {
    invalid('time intent is malformed.')
  }
  if (typeof value.departureTime !== 'string' || !validTime(value.departureTime)) {
    invalid('departure time must use HH:MM.')
  }
  if (value.leaveNow !== undefined
    && (value.version !== 4 || value.leaveNow !== true || value.timeIntent !== 'depart-at')) {
    invalid('leave-now intent is malformed.')
  }
  return {
    version: 4,
    timeIntent: value.version === 4 ? value.timeIntent as CurrentPlannerDraft['timeIntent'] : 'depart-at',
    ...(value.leaveNow === true ? { leaveNow: true as const } : {}),
    origins,
    venueType: value.venueType,
    preference: value.preference,
    maximumMinutes: value.maximumMinutes as PlannerDraft['maximumMinutes'],
    departureDate: value.departureDate ?? newYorkDateTime().date,
    departureTime: value.departureTime,
  }
}

function parsePoint(value: unknown): LocationPoint {
  if (!isRecord(value)
    || typeof value.displayName !== 'string' || !value.displayName || value.displayName.length > 300
    || typeof value.type !== 'string' || !value.type || value.type.length > 100
    || typeof value.lat !== 'number' || !Number.isFinite(value.lat) || value.lat < -90 || value.lat > 90
    || typeof value.lon !== 'number' || !Number.isFinite(value.lon) || value.lon < -180 || value.lon > 180) {
    invalid('an origin point is malformed.')
  }
  return {
    displayName: value.displayName,
    type: value.type,
    lat: value.lat,
    lon: value.lon,
  }
}

function parseChoice(value: unknown): Exclude<TravelChoice, { kind: 'none' }> {
  if (!isRecord(value)) invalid('a travel choice is malformed.')
  if (value.kind === 'permissions' && Array.isArray(value.modes)) {
    return { kind: 'permissions', modes: parsePermissions(value.modes) }
  }
  invalid('a travel choice is malformed.')
}

function migrateLegacyChoice(value: unknown): Exclude<TravelChoice, { kind: 'none' }> {
  if (!isRecord(value)) invalid('a travel choice is malformed.')
  if (value.kind === 'bike-share') return { kind: 'permissions', modes: ['bike-share'] }
  if (value.kind === 'road' && (value.mode === 'taxi' || value.mode === 'bike')) {
    return { kind: 'permissions', modes: [value.mode] }
  }
  if (value.kind === 'scheduled' && Array.isArray(value.modes)
    && value.modes.length >= 1 && value.modes.length <= scheduledModes.length
    && value.modes.every((mode) => scheduledModes.includes(mode as typeof scheduledModes[number]))
    && new Set(value.modes).size === value.modes.length
    && (value.bicycleAccess === undefined || value.bicycleAccess === true)
    && (value.bikeShareAccess === undefined || value.bikeShareAccess === true)) {
    return {
      kind: 'permissions',
      modes: canonicalPermissions([
        'walk',
        ...value.modes as ScheduledModeSet,
        ...(value.bicycleAccess ? ['bike' as const] : []),
        ...(value.bikeShareAccess ? ['bike-share' as const] : []),
      ]),
    }
  }
  invalid('a travel choice is malformed.')
}

function parsePermissions(value: unknown[]): TravelPermissionSet {
  if (value.length < 1 || value.length > travelModes.length
    || value.some((mode) => !travelModes.includes(mode as TravelMode))
    || new Set(value).size !== value.length) {
    invalid('travel permissions are malformed.')
  }
  return canonicalPermissions(value as TravelMode[])
}

function canonicalPermissions(modes: TravelMode[]): TravelPermissionSet {
  const selected = new Set(modes)
  return travelModes.filter((mode) => selected.has(mode)) as TravelPermissionSet
}

function validTime(value: string): boolean {
  const match = /^(\d{2}):(\d{2})$/.exec(value)
  return Boolean(match && Number(match[1]) <= 23 && Number(match[2]) <= 59)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function invalid(message: string): never {
  throw new Error(`Invalid planner draft: ${message}`)
}
